import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Flex,
    MenuTrigger,
    ActionButton,
    Menu,
    Item,
    Grid,
    Content,
    ProgressCircle,
    View,
    Heading,
    Divider,
    Dialog,
    DialogTrigger,
    Text,
} from '@adobe/react-spectrum';
import { SORT_MAP } from '../../data/redux/slices/leaderboardSlice';
import './ResultsListing.css';
import '../filterpanel/mana-cost.css';
import { setRouteChangeRefreshPending } from '../../data/redux/slices/appSlice';
import { getAnalytics, logEvent } from 'firebase/analytics';
import isMobile from 'ismobilejs';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';
import ChevronUp from '@spectrum-icons/workflow/ChevronUp';
import DownloadFromCloud from '@spectrum-icons/workflow/DownloadFromCloud';
import { FilterPanelTrigger, FilterPanelBody, FilterPanelPopover } from '../filterpanel/FilterPanel';
import Filter from '@spectrum-icons/workflow/Filter';
import { GenericLoadingDialog } from '../genericLoadingDialog/GenericLoadingDialog';
import { NavLink } from '../common/NavLink';

export function ResultsListing(props) {
    const dispatch = useDispatch();
    const sortBy = props?.sortBy;
    const selectedListIndex = props?.selectedListIndex;

    const isImporting = useSelector((state) => state.import.isImporting);
    const routeChangeRefreshPending = useSelector((state) => state.app.routeChangeRefreshPending);
    const mobile = isMobile(window.navigator).phone;
    const [filterOpen, setFilterOpen] = useState(false);

    const handleSortChange = (sortIndex) => {
        try {
            logEvent(getAnalytics(), `setSort_${sortIndex.toUpperCase()}`);
        } catch (error) {}

        props?.handleSortChange(sortIndex);
    };

    const handleSortDirectionChange = () => {
        const direction =
            props.sortDirection === SORT_MAP.DIRECTION.DESC ? SORT_MAP.DIRECTION.ASC : SORT_MAP.DIRECTION.DESC;

        try {
            logEvent(getAnalytics(), `setSort_direction_${direction.toUpperCase()}`);
        } catch (error) {}

        props?.handleSortDirectionChange && props?.handleSortDirectionChange(direction);
    };

    if (routeChangeRefreshPending === props?.routePath) {
        if (selectedListIndex && selectedListIndex > 0) {
            dispatch(setRouteChangeRefreshPending(null));
        }
    }

    const isFetching = props?.loadingState === 'loading' || props?.loadingState === 'loadingMore';

    const displayProgressBar = isImporting;
    const progressDialogHeader = 'Importing deck...';

    const minHeight = window.innerHeight - 70;

    const containerWidth = props?.containerWidth ?? 1400;
    const containerMaxWidth = props?.containerMaxWidth ?? `calc(100vw - 20px)`;
    const outerWidth = props?.outerWidth ?? '100vw';
    const outerMarginTop = props?.outerMarginTop ?? 50;
    const transparentContainer = Boolean(props?.transparentContainer);

    const getPerksStyling = (perks) => {
        const deckRowHighlighting = perks?.deckRowHighlighting; //.replace('%, 1)', '%, 0.5)');
        if (perks) {
            let retValue = {};

            if (perks?.useDeckRowHighlighting) {
                retValue = {
                    ...retValue,
                    background: `linear-gradient(to right, ${deckRowHighlighting} 0%, rgba(0, 0, 0, 0) 65%)`, //, rgba(0, 0, 0, 0) 75%, ${deckRowHighlighting} 100%)`,
                };
            }
            return retValue;
        }

        return {};
    };
    return (
        <div id={props.id}>
            <Flex
                direction="column"
                justifyContent={'space-around'}
                alignItems={transparentContainer ? 'stretch' : 'center'}
                width={outerWidth}
                marginTop={outerMarginTop}
            >
                {displayProgressBar && <GenericLoadingDialog eyebrow="Import" title="Importing deck..." />}

                <View
                    flex={1}
                    paddingTop="size-0"
                    UNSAFE_className={transparentContainer ? undefined : 'panelGradient'}
                    UNSAFE_style={transparentContainer ? { backgroundColor: 'transparent' } : undefined}
                    minHeight={minHeight}
                    maxWidth={containerMaxWidth}
                    width={containerWidth}
                >
                    <div className={'stickyResultsToolBar'}>
                        <Flex
                            direction={'column'}
                            UNSAFE_style={{
                                backgroundColor: transparentContainer ? 'transparent' : '#141414',
                                paddingTop: transparentContainer ? 4 : 10,
                                paddingBottom: transparentContainer ? 6 : 15,
                            }}
                        >
                            <Flex
                                direction={'row'}
                                columnGap={12}
                                alignItems={'center'}
                                UNSAFE_className="resultsToolbarRow"
                            >
                                {props?.showFilterPanel ? (
                                    transparentContainer ? (
                                        <FilterPanelPopover
                                            filters={props.filters}
                                            context={props?.filterContext || 'decks'}
                                            isOpen={filterOpen}
                                            onOpenChange={setFilterOpen}
                                            onFiltersChange={(evn) => props.onFiltersChange(evn)}
                                            onClear={(fresh) => props.onFiltersChange(fresh)}
                                            hideQueryInput={props?.hideQueryInput}
                                        />
                                    ) : (
                                        <FilterPanelTrigger
                                            filters={props.filters}
                                            context={props?.filterContext || 'decks'}
                                            isOpen={filterOpen}
                                            onToggle={() => setFilterOpen((v) => !v)}
                                            onClear={(fresh) => props.onFiltersChange(fresh)}
                                        />
                                    )
                                ) : (
                                    <div>&nbsp;</div>
                                )}
                                {props?.showFilterPanel && <span className="resultsToolbarPipe" />}
                                <Flex direction="row" alignSelf="flex-end" columnGap={8} alignItems="center">
                                    <span className="resultsToolbarLabel">SORT //</span>
                                    <MenuTrigger>
                                        <ActionButton UNSAFE_className="resultsToolbarButton resultsToolbarButton--value">
                                            {sortBy.label}
                                        </ActionButton>
                                        <Menu
                                            disabledKeys={[SORT_MAP.LIKECOUNT.enum]}
                                            onAction={(key) => handleSortChange(key)}
                                        >
                                            <Item key={SORT_MAP.POWERLEVEL.enum}>{SORT_MAP.POWERLEVEL.label}</Item>
                                            <Item key={SORT_MAP.BRACKET.enum}>{SORT_MAP.BRACKET.label}</Item>
                                            <Item key={SORT_MAP.SALT.enum}>{SORT_MAP.SALT.label}</Item>
                                            {props?.includeSortByCount && (
                                                <Item key={SORT_MAP.COUNT.enum}>{SORT_MAP.COUNT.label}</Item>
                                            )}
                                            {props?.includeSortByIngestDate && (
                                                <Item key={SORT_MAP.INGESTDATE.enum}>{SORT_MAP.INGESTDATE.label}</Item>
                                            )}
                                            {props?.includeSortByLikeCount && (
                                                <Item key={SORT_MAP.LIKECOUNT.enum}>
                                                    {SORT_MAP.LIKECOUNT.label} (soon)
                                                </Item>
                                            )}
                                        </Menu>
                                    </MenuTrigger>
                                    <ActionButton
                                        onPress={handleSortDirectionChange}
                                        aria-label={
                                            props?.sortDirection === SORT_MAP.DIRECTION.DESC
                                                ? 'Sort descending — toggle to ascending'
                                                : 'Sort ascending — toggle to descending'
                                        }
                                        UNSAFE_className="resultsToolbarButton resultsToolbarButton--icon"
                                    >
                                        {props?.sortDirection === SORT_MAP.DIRECTION.DESC ? (
                                            <ChevronDown />
                                        ) : (
                                            <ChevronUp />
                                        )}
                                    </ActionButton>
                                </Flex>
                            </Flex>
                            {props?.showFilterPanel && !transparentContainer && (
                                <FilterPanelBody
                                    isOpen={filterOpen}
                                    onClose={() => setFilterOpen(false)}
                                    onFiltersChange={(evn) => props.onFiltersChange(evn)}
                                    filters={props.filters}
                                    context={props?.filterContext || 'decks'}
                                    hideQueryInput={props?.hideQueryInput}
                                    transparentContainer={transparentContainer}
                                />
                            )}
                        </Flex>
                    </div>
                    <Grid
                        flex={1}
                        justifyContent={transparentContainer ? 'stretch' : 'center'}
                        rowGap={0}
                        maxWidth={containerMaxWidth}
                        width={containerWidth}
                    >
                        {(props?.listItems || []).length === 0 && !isFetching && (
                            <View UNSAFE_className="resultsEmptyState" paddingTop="size-400" paddingBottom="size-400">
                                <Flex direction="column" alignItems="center" gap="size-100">
                                    <span className="resultsEmptyEyebrow">No matches</span>
                                    <span className="resultsEmptyTitle">
                                        {props?.emptyStateTitle || 'Nothing here yet'}
                                    </span>
                                    <span className="resultsEmptyHint">
                                        {props?.emptyStateHint || 'Try loosening your filters or clearing the search.'}
                                    </span>
                                </Flex>
                            </View>
                        )}
                        {props?.listItems.map((item) => (
                            <NavLink
                                key={item.id}
                                to={props?.itemRoute ? props.itemRoute(item) : `/details/deck/${item.id}`}
                                onClick={() => {
                                    props?.handleIndexSelection && props.handleIndexSelection(item?.index);
                                    props?.handleSelectionChange && props.handleSelectionChange(item.id || null);
                                }}
                                style={{
                                    flex: '3 1 100%',
                                    width: transparentContainer ? 'calc(100% - 10px)' : containerWidth,
                                    maxWidth: containerMaxWidth,
                                    textDecoration: 'none',
                                    ...(transparentContainer ? { margin: '0 5px' } : {}),
                                }}
                            >
                                <View
                                    flex={1}
                                    borderWidth="thin"
                                    // borderColor="light"
                                    borderRadius="medium"
                                    padding={transparentContainer ? 0 : 'size-200'}
                                    // backgroundColor="gray-75"
                                    paddingTop={transparentContainer ? 0 : 1}
                                    paddingBottom={transparentContainer ? 0 : 1}
                                    paddingStart={transparentContainer ? 0 : 5}
                                    paddingEnd={transparentContainer ? 0 : 5}
                                    minHeight="0px"
                                    marginStart={transparentContainer ? 0 : 10}
                                    marginEnd={transparentContainer ? 0 : 10}
                                    marginBottom={transparentContainer ? 2 : 5}
                                    UNSAFE_className="row"
                                    UNSAFE_style={{ ...getPerksStyling(item?.perks || null) }}
                                >
                                    {/*<span className={"row"}>*/}
                                    <Flex
                                        flex={1}
                                        direction={'column'}
                                        UNSAFE_style={{
                                            backgroundColor: transparentContainer ? 'transparent' : '#000000',
                                            borderRadius: '10px',
                                            ...(transparentContainer ? { paddingLeft: 12, paddingRight: 12 } : {}),
                                        }}
                                    >
                                        <Flex direction={'row'} columnGap={15}>
                                            {props?.columns.map((col) => (
                                                <span key={`${item.id}_${col.uid}`} style={{ ...col }}>
                                                    {props?.cellRenderer(item, col.uid, {
                                                        compact: transparentContainer,
                                                    })}
                                                </span>
                                            ))}
                                        </Flex>
                                        {props?.cellRenderer(item, 'row_footer', {
                                            compact: transparentContainer,
                                        })}
                                    </Flex>
                                </View>
                            </NavLink>
                        ))}
                    </Grid>
                    <Flex flex={1} direction={'row'} justifyContent={'center'}>
                        <ActionButton
                            onPress={props?.handleLoadMore}
                            isDisabled={isFetching || !props.hasMore}
                            marginTop={20}
                            minWidth={300}
                            aria-label="Load more"
                            marginBottom={20}
                        >
                            {!isFetching ? (
                                <Flex direction={'row'} justifyContent={'center'} width={'100%'}>
                                    Load More
                                    <DownloadFromCloud />
                                </Flex>
                            ) : (
                                <Flex direction={'row'} justifyContent={'center'} width={'100%'}>
                                    <ProgressCircle isIndeterminate="true" size="S" />
                                </Flex>
                            )}
                        </ActionButton>
                    </Flex>
                </View>
            </Flex>
        </div>
    );
}
